import React from "react";
import "../PaymentReceipts/receipt.css";
import is_money from "../../helpers/is_money";
import useAuthStore from "../../stores/useAuthStore";
import { staffBackend } from "../../helpers/uri";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import ErrorResponse from "../../components/ErrorResponse";

export const Receipt = () => {
    const { reference } = useParams();
    const { settings } = useAuthStore();

    const { data: transaction, isLoading, error } = useQuery({
        queryKey: ["walletReceipt", reference],
        queryFn: async () => {
            const req = await axiosget(`ms_student_wallet_transactions/receipt/${reference}`);
            return req.data;
        },
        enabled: !!reference
    });

    // Student info is now inside the transaction object from the backend population
    const student = transaction?.student;

    if (isLoading) return <Loader />;
    if (error) return <ErrorResponse message="Failed to load receipt" />;
    if (!transaction) return <ErrorResponse message="Transaction not found" />;

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-NG", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const gatewayName = transaction?.payment?.gateway || "Online Payment";
    const amount = transaction?.amount || 0;

    const matricDisplay = student?.matricnumber || student?.studentid;

    return (
        <div className="receipt-container">
            <div className="receipt-wrapper">
                {/* Header */}
                <div className="receipt-header">
                    <div className="header-logo">
                        <img src={settings?.logo ? `${staffBackend?.slice(0, -1)}${settings.logo}` : "/images/college-logo.png"} alt="Institution Logo" />
                    </div>
                    <div className="header-info">
                        <h1>{settings?.institutionName || "ADAMU TAFAWA BALEWA COLLEGE OF EDUCATION, KANGERE"}</h1>
                        <p className="sub-title">BURSARY DEPARTMENT</p>
                        {/* Changed from DIRECTORATE OF STUDIES as per context */}

                        <p className="address">MAIN CAMPUS, ALONG BAUCHI-GOMBE ROAD, KANGERE, BAUCHI STATE</p>
                    </div>
                </div>

                {/* Title */}
                <div className="receipt-title">
                    <h2>OFFICIAL PAYMENT RECEIPT</h2>
                    <div className="receipt-number">Receipt #{transaction?.trx?.slice(-8).toUpperCase()}</div>
                </div>

                {/* Status Badge */}
                <div className={`status-badge ${transaction?.status === 'success' ? "paid" : "pending"}`}>
                    {transaction?.status === 'success' ? "✓ PAYMENT CONFIRMED" : "⏳ PENDING"}
                </div>

                {/* Student & Payment Info */}
                <div className="info-grid">
                    <div className="info-section">
                        <h3>Student Information</h3>
                        <div className="info-row">
                            <span className="label">Matric No:</span>
                            <span className="value">{matricDisplay || "N/A"}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Full Name:</span>
                            <span className="value">{`${student?.firstname || ""} ${student?.lastname || ""} ${student?.othername || ""}`.trim() || "N/A"}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Department:</span>
                            <span className="value">{student?.department?.name || student?.programme?.department?.name || "N/A"}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Level:</span>
                            <span className="value">{student?.level || "N/A"}</span>
                        </div>
                    </div>

                    <div className="info-section">
                        <h3>Payment Details</h3>
                        <div className="info-row">
                            <span className="label">Description:</span>
                            <span className="value">{transaction?.purpose || "Wallet Funding"}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Transaction Ref:</span>
                            <span className="value reference">{transaction?.trx || "N/A"}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Payment Method:</span>
                            <span className="value text-uppercase">{gatewayName}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Amount Paid:</span>
                            <span className="value amount">{is_money(amount)}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Payment Date:</span>
                            <span className="value">{formatDate(transaction?.payment?.created_at || transaction?.updatedAt)}</span>
                        </div>
                    </div>
                </div>

                {/* Fee Items Table */}
                <div className="items-section">
                    <h3>Payment Breakdown</h3>
                    <table className="items-table">
                        <thead>
                            <tr>
                                <th>S/N</th>
                                <th>Description</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>1</td>
                                <td>{transaction?.purpose || "Wallet Fund Deposit"}</td>
                                <td className="amount-cell">{is_money(amount)}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td></td>
                                <td><strong>Total Paid</strong></td>
                                <td className="total-cell">{is_money(amount)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Declaration */}
                <div className="declaration">
                    <p>
                        This is to certify that the sum of <strong>{is_money(amount)}</strong> was received from{" "}
                        <strong>{`${student?.firstname || ""} ${student?.lastname || ""}`.trim()}</strong> {matricDisplay ? `(${matricDisplay})` : ""}
                        for wallet funding via {gatewayName}.
                    </p>
                    <p className="warning">⚠️ THIS RECEIPT IS ELECTRONICALLY GENERATED</p>
                </div>

                {/* Signatures */}
                <div className="signatures">
                    <div className="signature-block">
                        <div className="signature-line"></div>
                        <p className="signature-title">Student's Signature</p>
                        <p className="signature-name">{`${student?.firstname || ""} ${student?.lastname || ""}`.trim()}</p>
                    </div>
                    <div className="signature-block">
                        <div className="signature-line"></div>
                        <p className="signature-title">Bursar's Signature</p>
                        <p className="signature-name">Head of Bursary Department</p>
                    </div>
                </div>

                {/* Footer */}
                <div className="receipt-footer">
                    <p>This receipt is computer-generated and valid without signature when verified online.</p>
                    <p>For verification, visit: {window.location.host}</p>
                </div>

                {/* Print Button */}
                <button className="print-btn" onClick={() => window.print()}>
                    🖨️ Print Receipt
                </button>
            </div>
        </div>
    );
};
